// Copyright (c) 2025 Juan Beltrán
// Licensed under the MIT License. See LICENSE file for details.

import React, { useEffect, useState } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { Settings, Menu, X, SquarePen } from 'lucide-react';
import useCookieConsent from '../hooks/useCookieConsent';
import { ModelSelector } from '../components/ModelSelector';
import { ConfigPanel } from '../components/ConfigPanel';
import { Chat } from '../components/Chat';
import Auth from '../components/Auth';
import UserProfile from '../components/UserProfile';
import { useStore } from '../store/useStore';
import { translations } from '../utils/translations';
import { ChatHistory } from '../components/ChatHistory';
import LandingPage from '../components/LandingPage';
import UsageDashboard from '../components/UsageDashboard';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
 

export function Home() {
  const { } = useCookieConsent();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isUsageDashboardOpen, setIsUsageDashboardOpen] = useState(false);
  const {
    toggleConfigPanel,
    initializeModels,
    loadChat,
    isConfigPanelOpen,
    user,
    setUser,
    language,
    isLoading,
    clearMessages,
    startNewChat
  } = useStore();

  // Add this useEffect hook
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
    setUser(currentUser);
  });
  return () => unsubscribe();
}, [setUser]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleUsageDashboard = () => {
    const isClosing = isUsageDashboardOpen;
    setIsUsageDashboardOpen(!isUsageDashboardOpen);
    if (isClosing) {
      window.location.reload();
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // 1024px is the lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    initializeModels();
  }, []);

 

  // Show loading state while initializing
  if (isLoading) {
    return (
      <div className="h-screen bg-gray-100 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 dark:text-dark-fg">{translations[language].welcomeToAiChat}</h2>
          <div className="animate-pulse dark:text-dark-fg">Loading...</div>
        </div>
      </div>
    );
  }

  // Show LandingPage for no user, temp user, or when user is being cleared
  if (!user || user.uid === 'temp') {
    return <LandingPage />;
  }

  // Show verification required screen if email is not verified
  if (user && !user.emailVerified) {
    return (
      <div className="h-screen bg-gray-100 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold mb-4 dark:text-dark-fg">
            {translations[language].emailVerificationRequired}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {translations[language].checkEmail}
          </p>
          <div className="space-y-4">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {translations[language].refreshPage}
            </button>
            
            <button
              onClick={() => {
                // Sign out and return to landing page
                auth.signOut();
                setUser(null);
              }}
              className="w-full text-blue-500 hover:underline py-2"
            >
              {translations[language].backToHome}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-[100dvh] bg-gray-50 dark:bg-gray-900">
        {/* Mobile Header */}
        <div className="bg-gray-50 dark:bg-gray-800 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <button
                onClick={toggleSidebar}
                className="p-2 mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                aria-label="Toggle sidebar"
              >
                {isSidebarOpen ? 
                  <X className="w-5 h-5" /> : 
                  <Menu className="w-5 h-5" />
                }
              </button>
              <img
                src="/icons/camelon-negro-logo-transparente.png"
                alt="Camelon Logo"
                className="h-8 w-auto"
              />
            </div>
            <UserProfile onUsageClick={toggleUsageDashboard} />
          </div>
        </div>

        {/* Mobile Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-gray-800
          transform transition-transform duration-300 ease-in-out shadow-lg
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Sidebar header with logo */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="/icons/camelon-negro-logo-transparente.png"
                alt="Camelon Logo"
                className="h-8 w-auto mr-3"
              />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Camelon AI</h1>
            </div>
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* New chat button */}
          <div className="p-4">
            <button
              onClick={() => {
                clearMessages();
                startNewChat();
                setIsSidebarOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
            >
              <SquarePen className="w-5 h-5" />
              <span className="font-medium">{translations[language].newChat}</span>
            </button>
          </div>
          
          {/* Chat history */}
          <div className="flex-1 overflow-y-auto h-[calc(100%-12rem)]">
            <div className="px-4 py-2 flex justify-between items-center">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {translations[language].history}
              </h2>
            </div>
            <div className="px-3 pb-4">
              <ChatHistory />
            </div>
          </div>

          {/*
            IMPORTANT LICENSE NOTICE:
            The following attribution section (by JB and LinkedIn link) is a required part of the MIT license
            and must not be removed or modified without explicit permission from Juan Beltrán.
            For licensing inquiries, please contact through LinkedIn: https://www.linkedin.com/in/juan-beltran-ai/
          */}
          <div className="py-3 px-4 mt-auto bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">by JB v 1.0</span>
              <a
                href="https://www.linkedin.com/in/juan-beltran-ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 hover:opacity-75 transition-opacity"
              >
                <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Main Content */}
        <div className="flex flex-col h-[calc(100dvh-4rem)]">
          <div className="py-3 px-4 bg-gray-50 dark:bg-gray-800 shadow-sm">
            <ModelSelector />
          </div>

          <div className="flex-1 overflow-hidden">
            <Chat />
          </div>
        </div>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
            onClick={toggleSidebar}
          />
        )}

        <ConfigPanel />
        
        {/* Usage Dashboard Modal */}
        {isUsageDashboardOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <UsageDashboard onClose={toggleUsageDashboard} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="flex h-[100dvh] bg-gray-50 dark:bg-gray-900">
      {/* Desktop Sidebar */}
      <div className="w-64 bg-white dark:bg-gray-800 flex flex-col shadow-sm">
        {/* Logo */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center">
            <img
              src="/icons/camelon-negro-logo-transparente.png"
              alt="Camelon Logo"
              className="h-10 w-auto mr-3"
            />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Camelon AI</h1>
          </div>
        </div>
        
        {/* New chat button */}
        <div className="p-4">
          <button
            onClick={() => {
              clearMessages();
              startNewChat();
            }}
            className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
          >
            <SquarePen className="w-5 h-5" />
            <span className="font-medium">{translations[language].newChat}</span>
          </button>
        </div>
        
        {/* Chat history */}
        <div className="flex-1 overflow-hidden">
          <div className="px-4 py-3 flex justify-between items-center">
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {translations[language].history}
            </h2>
          </div>
          <div className="px-3 overflow-y-auto h-full pb-4">
            <ChatHistory />
          </div>
        </div>
        
        {/*
          IMPORTANT LICENSE NOTICE:
          The following attribution section (by JB and LinkedIn link) is a required part of the MIT license
          and must not be removed or modified without explicit permission from Juan Beltrán.
          For licensing inquiries, please contact through LinkedIn: https://www.linkedin.com/in/juan-beltran-ai/
        */}
        <div className="mt-auto py-3 px-4 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">by JB v 1.0</span>
            <a
              href="https://www.linkedin.com/in/juan-beltran-ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 hover:opacity-75 transition-opacity"
            >
              <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      
      {/* Desktop Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 dark:bg-gray-800 py-3 px-6 flex justify-between items-center shadow-sm">
          <ModelSelector />
          <UserProfile onUsageClick={toggleUsageDashboard} />
        </div>
        
        {/* Chat area */}
        <div className="flex-1 overflow-hidden">
          <Chat />
        </div>
      </div>

      {/* Config Panel */}
      <ConfigPanel />

      {/* Usage Dashboard Modal */}
      {isUsageDashboardOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <UsageDashboard onClose={toggleUsageDashboard} />
          </div>
        </div>
      )}
    </div>
  );
}